import { useEffect } from "react";
import { X } from "lucide-react";
import { useSwipeable } from "react-swipeable";
import useTranslate from "../../hooks/useTranslate";

// Shared mobile bottom-sheet. Slides up over a dimmed backdrop; closes on
// backdrop tap, the close button, or a downward swipe on the sheet. Kept
// generic (title + children + optional footer) so other mobile surfaces can
// reuse it. No portal — rendered inline with a high z-index, matching the
// app's existing modal approach.
// The sheet is the narrow-width counterpart of a desktop surface, so it hides
// exactly where that surface takes over. Almost every caller hands over at md
// and keeps the default; the participants rail hands over at lg, so it opts in
// with hiddenFrom="lg". Both class names are written out in full on purpose —
// a template like `${hiddenFrom}:hidden` would be purged out of the stylesheet,
// and the sheet would silently render at 0×0.
const HIDE_FROM = { md: "md:hidden", lg: "lg:hidden" };

const BottomSheet = ({ open, onClose, title, children, footer, hiddenFrom = "md" }) => {
  const { t } = useTranslate();
  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const swipeHandlers = useSwipeable({
    onSwipedDown: () => onClose && onClose(),
    delta: 50,
    trackTouch: true,
    trackMouse: false,
  });

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-50 ${HIDE_FROM[hiddenFrom] || HIDE_FROM.md}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        {...swipeHandlers}
        className="absolute inset-x-0 bottom-0 bg-white rounded-t-[22px] max-h-[85vh] flex flex-col shadow-[0_-12px_40px_rgba(16,24,40,0.18)]"
      >
        {/* Grabber — 38×4 #D5DBE7 per V2 artboards (1l/1n) */}
        <div className="pt-3 flex justify-center">
          <span className="h-1 w-[38px] rounded-full bg-[#D5DBE7]" />
        </div>

        <div className="flex items-center justify-between px-[18px] pt-2 pb-3">
          <h2 className="text-[16px] font-semibold text-[#101828]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[#667085] hover:text-[#101828]"
            aria-label={t("Close")}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-[18px] overflow-y-auto flex-1">{children}</div>

        {footer && (
          <div className="px-[18px] py-4 border-t border-[#E6EAF2]">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default BottomSheet;
